import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type FC,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useStorePrice } from '@/common/hooks/apple-price';
import { Loader } from '@/components/ui/Loader/Loader';

import { formatUsd } from './format';

import css from './styles.module.scss';

import foldBottom from '/img/shop/v2/sale-fold-bottom.svg';
import foldLeft from '/img/shop/v2/sale-fold-left.svg';
import shuffleIcon from '/img/shop/v2/shuffle.svg';

/** Orange tag in the top-right corner of a button: "-50%", "DAILY". */
const SaleTag: FC<{
  text: string;
  className?: string;
  /** Tag of a tile (VIP period): only the top-left fold */
  leftFoldOnly?: boolean;
}> = ({ text, className, leftFoldOnly }) => (
  <div className={cn(css.saleTag, className)}>
    <span>{text}</span>
    <div className={css.foldLeft}>
      <img src={foldLeft} alt="" />
    </div>
    {!leftFoldOnly && (
      <div className={css.foldBottom}>
        <img src={foldBottom} alt="" />
      </div>
    )}
  </div>
);

type BadgeColor = 'purple' | 'green' | 'yellow' | 'red' | 'blue';

// [badge, fold, text] colours of the corner badges in the design
const badgeColors: Record<BadgeColor, [string, string, string]> = {
  purple: ['#4e2b82', '#2f1356', '#fff'],
  green: ['#3f760a', '#244604', '#fff'],
  yellow: ['#ffa00f', '#a35e0a', '#a35e0a'],
  red: ['#a61919', '#8c0505', '#fff'],
  blue: ['#204080', '#0f2550', '#fff'],
};

interface ShopBadgeProps {
  text: string;
  color: BadgeColor;
  /** Sticks 8px out of the card top, with a fold on the left */
  raised?: boolean;
  size?: 'md' | 'sm';
  className?: string;
}

/** Corner badge of a card: EXCLUSIVE, POPULAR, 230% VALUE… */
const ShopBadge: FC<ShopBadgeProps> = ({
  text,
  color,
  raised,
  size = 'md',
  className,
}) => {
  const [bg, fold, textColor] = badgeColors[color];

  return (
    <div
      className={cn(
        css.badge,
        raised && css.raised,
        size === 'sm' && css.sm,
        className,
      )}
      style={{ '--badge-color': bg, color: textColor } as CSSProperties}
    >
      <span>{text}</span>
      {raised && (
        <div className={css.raisedFold}>
          <svg width="10.928" height="6.928" viewBox="0 0 10.9282 6.92821">
            <path
              d="M6.92818 0L10.9282 6.92821L0 3.99998L6.92818 0Z"
              fill={fold}
            />
          </svg>
        </div>
      )}
    </div>
  );
};

interface ShopBuyButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** USD price; ignored when `label` is set */
  usd?: number;
  /** Shown instead of a price ("Free", "13h : 34m"…) */
  label?: ReactNode;
  /** Crossed-out USD price before the discount */
  oldUsd?: number;
  /** Discount in percent for the orange tag */
  discount?: number;
  /** Any other tag text ("DAILY") — kept in the App Store build */
  tag?: string;
  /** Small tab above the button ("23h : 12m") */
  timer?: ReactNode;
  /** Something before the price (a T-Gem icon) */
  icon?: ReactNode;
  variant?: 'green' | 'purple' | 'locked' | 'dashed';
  isLoading?: boolean;
  wrapperClassName?: string;
}

/**
 * Shop price button. In the iOS app the App Store price is shown and the
 * made-up "before" price and discount tag are dropped (App Review rule),
 * just like the other buy buttons of the game.
 */
const ShopBuyButton: FC<ShopBuyButtonProps> = ({
  usd,
  label,
  oldUsd,
  discount,
  tag,
  timer,
  icon,
  variant = 'green',
  isLoading,
  className,
  wrapperClassName,
  ...props
}) => {
  const storePrice = useStorePrice(label === undefined ? usd : undefined);
  const showDiscount = !storePrice;
  const tagText =
    tag ?? (showDiscount && discount ? `-${discount}%` : undefined);

  return (
    <div className={cn('relative w-full', wrapperClassName)}>
      {timer && <div className={css.timerTab}>{timer}</div>}
      <button
        type="button"
        {...props}
        className={cn(css.buy, css[variant], className)}
      >
        {isLoading ? (
          <Loader className="!size-5" />
        ) : (
          <>
            {icon}
            {label === undefined &&
              showDiscount &&
              !!oldUsd &&
              oldUsd > (usd ?? 0) && (
                <span className={css.oldPrice}>{formatUsd(oldUsd)}</span>
              )}
            <span className={css.price}>
              {label ?? storePrice ?? (usd !== undefined ? formatUsd(usd) : '')}
            </span>
          </>
        )}
        {tagText && <SaleTag text={tagText} />}
      </button>
    </div>
  );
};

const ShuffleButton: FC<
  ButtonHTMLAttributes<HTMLButtonElement> & { isLoading?: boolean }
> = ({ isLoading, className, ...props }) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      {...props}
      className={cn(css.shuffle, className)}
      disabled={isLoading || props.disabled}
    >
      {isLoading ? (
        <Loader className="!size-5" />
      ) : (
        <>
          {t('Shuffle')}
          <img src={shuffleIcon} alt="" className="size-4" />
        </>
      )}
    </button>
  );
};

/** Big centred heading of a shop section ("Shine with T-Gems!"). */
const ShopTitle: FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => <h1 className={cn(css.title, className)}>{children}</h1>;

interface GlowProps {
  color: string;
  className?: string;
  blur?: number;
  style?: CSSProperties;
}

/** Blurred colour spot in a card background. */
const Glow: FC<GlowProps> = ({ color, className, blur = 50, style }) => (
  <div
    className={cn(css.glow, className)}
    style={
      {
        '--glow-color': color,
        '--glow-blur': `${blur}px`,
        ...style,
      } as CSSProperties
    }
  />
);

export {
  type BadgeColor,
  Glow,
  SaleTag,
  ShopBadge,
  ShopBuyButton,
  ShopTitle,
  ShuffleButton,
};
